import React, { ReactNode, createContext, useContext } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { useHistory } from 'react-router-dom';
import { useIcons } from '../../hooks/useIcons';
import { useCanvas, useInCanvas } from '../canvas/CanvasContext';
import { AgentSourceLink, parseSourceHref } from './AgentChatSource';
import {
  cellLink,
  extractCellHref,
  isNumericLabel,
  isNumericValue,
  isTrackTable,
  parseTrackRows,
  splitMarkdownBlocks,
} from './AgentChatTables';
import { AgentChatTrackList } from './AgentChatTrackList';

const InTable = createContext(false);

const urlTransform = (url: string) => {
  if (/^(ch|serato|vdj|spotify|tidal|apple):/i.test(url)) return url;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  return defaultUrlTransform(url);
};

const AgentMarkdownLink: React.FC<{
  href?: string;
  children?: ReactNode;
}> = ({ href, children }) => {
  const { icons } = useIcons();
  const inTable = useContext(InTable);
  return (
    <AgentSourceLink href={href} icons={icons} inTable={inTable}>
      {children}
    </AgentSourceLink>
  );
};

const markdownComponents = {
  a: ({
    href,
    children,
    node,
  }: {
    href?: string;
    children?: ReactNode;
    node?: { properties?: { href?: unknown } };
  }) => {
    const fromNode = node?.properties?.href;
    const resolved =
      href || (typeof fromNode === 'string' ? fromNode : undefined);
    return <AgentMarkdownLink href={resolved}>{children}</AgentMarkdownLink>;
  },
};

const cellComponents = {
  ...markdownComponents,
  p: ({ children }: { children?: ReactNode }) => <>{children}</>,
};

const CellMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const linked = cellLink(text);
  if (linked && parseSourceHref(linked.href)) {
    return (
      <AgentMarkdownLink href={linked.href}>{linked.label}</AgentMarkdownLink>
    );
  }
  return (
    <ReactMarkdown components={cellComponents} urlTransform={urlTransform}>
      {text || '—'}
    </ReactMarkdown>
  );
};

const rowDestination = (row: string[]): string | undefined => {
  for (const cell of row) {
    const href = extractCellHref(cell);
    const dest = href ? parseSourceHref(href)?.to : undefined;
    if (dest) return dest;
  }
  return undefined;
};

const tableMarkdown = (headers: string[], rows: string[][]): string =>
  [
    `| ${headers.join(' | ')} |`,
    `| ${headers.map(() => '---').join(' | ')} |`,
    ...rows.map(
      (row) => `| ${headers.map((_, i) => row[i] ?? '').join(' | ')} |`,
    ),
  ].join('\n');

/** The rail is a preview; the canvas is where a long list is actually read. */
const CHAT_ROWS = 5;

const AgentResultsTable: React.FC<{
  headers: string[];
  rows: string[][];
}> = ({ headers, rows }) => {
  const history = useHistory();
  const canvas = useCanvas();
  const inCanvas = useInCanvas();
  const numeric = headers.map((header) => isNumericLabel(header));

  // Only shorten for the rail: in the canvas this is the full list, and with
  // no canvas to pop out to, trimming rows would just lose them.
  const trimmed = canvas && !inCanvas ? rows.slice(0, CHAT_ROWS) : rows;
  const hidden = rows.length - trimmed.length;

  // Four columns is roughly where the rail starts wrapping every cell.
  const popOut =
    canvas && !inCanvas && (hidden > 0 || headers.length >= 4)
      ? () =>
          canvas.openCanvas({
            id: `table-${headers.join('|')}-${rows.length}`,
            kind: 'table',
            title: 'Table',
            markdown: tableMarkdown(headers, rows),
          })
      : undefined;

  return (
    <div className="agent-results-table-wrap">
      <InTable.Provider value={true}>
        <table className="agent-results-table">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={`${header}-${index}`}
                  className={numeric[index] ? 'is-num' : undefined}
                >
                  <CellMarkdown text={header} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trimmed.map((row, rowIndex) => {
              const dest = rowDestination(row);
              return (
                <tr
                  key={rowIndex}
                  className={dest ? 'is-clickable' : undefined}
                  onClick={
                    dest
                      ? (event) => {
                          const target = event.target as HTMLElement;
                          if (
                            target.closest(
                              'a, ion-router-link, .agent-result-link',
                            )
                          ) {
                            return;
                          }
                          history.push(dest);
                        }
                      : undefined
                  }
                >
                  {headers.map((_, colIndex) => {
                    const cell = row[colIndex] ?? '';
                    return (
                      <td
                        key={colIndex}
                        className={
                          numeric[colIndex] || isNumericValue(cell)
                            ? 'is-num'
                            : undefined
                        }
                      >
                        <CellMarkdown text={cell} />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </InTable.Provider>
      {popOut && (
        <button type="button" className="agent-table-popout" onClick={popOut}>
          {hidden > 0 ? `Open all ${rows.length} in canvas` : 'Open in canvas'}
        </button>
      )}
    </div>
  );
};

export const AgentChatMarkdown: React.FC<{ content: string }> = ({
  content,
}) => (
  <>
    {splitMarkdownBlocks(content).map((block, index) =>
      block.type === 'table' && isTrackTable(block.headers, block.rows) ? (
        <AgentChatTrackList
          key={`tracks-${index}`}
          rows={parseTrackRows(block.headers, block.rows)}
        />
      ) : block.type === 'table' ? (
        <AgentResultsTable
          key={`table-${index}`}
          headers={block.headers}
          rows={block.rows}
        />
      ) : (
        <ReactMarkdown
          key={`md-${index}`}
          components={markdownComponents}
          urlTransform={urlTransform}
        >
          {block.text}
        </ReactMarkdown>
      ),
    )}
  </>
);

export default AgentChatMarkdown;
